import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { partnerNetworksApi } from '@/api/services';

/** Not a company id, so it cannot collide with one. Radix forbids an empty value. */
const NONE = '__none__';

/**
 * Whose site this is — the field that turns a location into a consignment node.
 *
 * Only companies we hold an active CUSTOMER partnership with are offered, which
 * is the same rule the server enforces before it writes. Offering every company
 * and rejecting most of them on save would be the usual way to get this wrong.
 *
 * When there are no customer partnerships at all the control is not shown as an
 * empty dropdown; it says what is missing and where to add it, because an empty
 * select reads as a loading failure.
 */
export function CustomerSiteField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (customerCompanyId: string | null) => void;
}) {
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    partnerNetworksApi
      .getAll()
      .then((res) => {
        const found = (res.asOwner ?? [])
          .filter((p) => p.status === 'ACTIVE' && p.partnerTypes.includes('CUSTOMER'))
          .map((p) => ({
            id: p.partnerCompanyId,
            name: p.partnerCompany?.displayName ?? p.partnerCompany?.name ?? p.partnerCompanyId,
          }));
        setCustomers(found);
      })
      .catch(() => setCustomers([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-2">
      <Label htmlFor="customer-site">Customer site</Label>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading customers…</p>
      ) : customers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No customer partnerships yet. Add one under Setup → Partner Networks with a partner type
          of Customer, and this location can then be marked as their site.
        </p>
      ) : (
        <Select
          value={value ?? NONE}
          onValueChange={(v) => onChange(v === NONE ? null : v)}
        >
          <SelectTrigger id="customer-site">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Ours, not a customer site</SelectItem>
            {customers.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <p className="text-xs text-muted-foreground">
        Setting a customer makes this a consignment node: stock stands here under contract and
        title passes when they call it off.
      </p>
    </div>
  );
}
